import React, { useState } from 'react';
import { Search, Filter, Calendar, Download, X } from 'lucide-react';

const TransactionFilters = ({
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  dateRange, setDateRange,
  amountRange, setAmountRange,
  onExport
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const clearAll = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ start: '', end: '' });
    setAmountRange({ min: '', max: '' });
  };

  const hasActiveFilters = searchTerm || statusFilter || dateRange.start || dateRange.end || amountRange.min || amountRange.max;

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'paid',       label: 'Paid' },
    { value: 'pending',    label: 'Pending' },
    { value: 'failed',     label: 'Failed' },
    { value: 'refunded',   label: 'Refunded' },
    { value: 'processing', label: 'Processing' },
    { value: 'cancelled',  label: 'Cancelled' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-3 md:p-4 mb-4">

      {/* ── Row 1: search + controls ── */}
      <div className="flex gap-2 items-center">

        {/* Search */}
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            placeholder="Search ID, customer, email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder:text-gray-300"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Status select */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="py-2 px-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white flex-shrink-0 min-w-[110px] md:min-w-[130px]"
        >
          {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {/* Advanced toggle */}
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg flex-shrink-0 transition-colors ${
            showAdvanced || hasActiveFilters
              ? 'bg-teal-50 border-teal-300 text-teal-700'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          <span className="hidden sm:inline text-sm">Filters</span>
          {hasActiveFilters && <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" />}
        </button>

        {/* Export */}
        <button
          onClick={onExport}
          className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg flex-shrink-0 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Export</span>
        </button>
      </div>

      {/* ── Row 2: advanced filters ── */}
      {showAdvanced && (
        <div className="mt-3 pt-3 border-t border-orange-100">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

            {/* Date range */}
            <div>
              <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                <Calendar className="w-3 h-3" /> Date Range
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                <input type="date" value={dateRange.start}
                  onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <input type="date" value={dateRange.end}
                  onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Amount range */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Amount (₹)</label>
              <div className="grid grid-cols-2 gap-1.5">
                <input type="number" placeholder="Min" value={amountRange.min}
                  onChange={e => setAmountRange(p => ({ ...p, min: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <input type="number" placeholder="Max" value={amountRange.max}
                  onChange={e => setAmountRange(p => ({ ...p, max: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Clear */}
            <div className="flex items-end">
              <button
                onClick={clearAll}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Clear All Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionFilters;